import React, { useMemo } from 'react';

import { useSectionPermissions } from '../PermissionAccessGuard/hooks/useSectionPermissions';

const Chevron = ({ open, size = 12, strokeWidth = 2 }: { open: boolean; size?: number; strokeWidth?: number }) => (
  <svg className={`cop-data-grid__filter-chevron${open ? ' cop-data-grid__filter-chevron--open' : ''}`}
    width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface DataGridActionsProps {
  searchValue: string;
  searchPlaceholder?: string;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSearchChange?: (value: string) => void;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  filterOptions?: { label: string; value: string }[];
  selectedFilter?: string;
  dropdownOpen: boolean;
  setDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onFilterChange?: (value: string) => void;
  dropdownRef: React.RefObject<HTMLDivElement>;
  actionButton?:
    | { type: 'action'; label: string; href: string; enabled: boolean }
    | { type: 'export'; label: string; enabled: boolean; options: { label: string; value: string; onClick: () => void }[] };
  exportDropdownOpen: boolean;
  setExportDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  exportDropdownRef: React.RefObject<HTMLDivElement>;
  hasActionButton: boolean;
  hasExportButton: boolean;
  gaShieldToggle?: { label: string; enableGaShieldToggle:boolean | string; checked: boolean; onChange: (checked: boolean) => void };
  addActionButton?:{ buttonKey:string;
  buttonType: string,
      buttonLabel: string,
      buttonUrl: string };
  gridType?: string;
  hasNoRows?: boolean;
}

const DataGridActions = ({
  searchValue, searchPlaceholder, handleSearchChange, onSearchChange, setSearchValue,
  filterOptions, selectedFilter, dropdownOpen, setDropdownOpen, onFilterChange, dropdownRef,
  actionButton, exportDropdownOpen, setExportDropdownOpen, exportDropdownRef,
  hasActionButton, hasExportButton, gaShieldToggle, addActionButton, gridType, hasNoRows,
}: DataGridActionsProps) => {
  const { permissions, isLoading } = useSectionPermissions();
  const isAgreementsGrid = gridType === 'agreements' || gridType === 'agreementsBySite';
  const canShowExport = !isAgreementsGrid || isLoading || permissions.canViewAgreementsExportDetails;

  const resolvedFilterOptions = useMemo(() => {
    if (!filterOptions || filterOptions.length === 0) return [];
    const myCaseValues = ['MyOpenCases', 'MyCases', 'MyClosedCases'];
    const companyCaseValues = ['CompanyOpenCases', 'CompanyCases', 'CompanyClosedCases'];
    return filterOptions.filter(opt => {
      if (myCaseValues.includes(opt.value))      return permissions.canViewMyCases;
      if (companyCaseValues.includes(opt.value)) return permissions.canViewCompanyCases;
      return true;
    });
  }, [filterOptions, permissions.canViewMyCases, permissions.canViewCompanyCases]);

  const selectedLabel = resolvedFilterOptions.find(o => o.value === selectedFilter)?.label ?? resolvedFilterOptions[0]?.label ?? '';

  return (
    <div className={`cop-data-grid__toolbar${hasActionButton ? ' cop-data-grid__toolbar--with-action' : ''}${hasExportButton ? ' cop-data-grid__toolbar--with-export' : ''}`}>
      {gridType !== 'userManagementRoles' && (
      <div className="cop-data-grid__search">
        <span className="cop-data-grid__search-icon">
          <span className="cop-data-grid__search-icon-img" aria-hidden="true" />
        </span>
        <input type="text" className={`cop-data-grid__search-input${searchValue ? ' cop-data-grid__search-input--has-value' : ''}`}
          placeholder={searchPlaceholder} value={searchValue} onChange={handleSearchChange} />
        {searchValue && (
          <button
            type="button"
            className="cop-data-grid__search-clear"
            aria-label="Clear search"
            onClick={() => {
              setSearchValue('');
              onSearchChange?.('');
            }}
          >
            &#10005;
          </button>
        )}
      </div>
      )}
      {resolvedFilterOptions.length > 0 && (
        <div className="cop-data-grid__filter" ref={dropdownRef}>
          <button type="button" className="cop-data-grid__filter-toggle"
            onClick={() => setDropdownOpen(prev => !prev)} aria-haspopup="menu" aria-expanded={dropdownOpen}>
            <span className="cop-data-grid__filter-icon" aria-hidden="true" />
            <span className="cop-data-grid__filter-label">{selectedLabel}</span>
            <span
              className={`cop-data-grid__filter-chevron-icon${dropdownOpen ? ' cop-data-grid__filter-chevron-icon--open' : ''}`}
              aria-hidden="true"
            />
          </button>
          {dropdownOpen && (
            <ul className="cop-data-grid__filter-list" role="menu">
              {resolvedFilterOptions.map(option => (
                <li
                  key={option.value}
                  role="menuitemradio"
                  aria-checked={option.value === selectedFilter}
                  className={`cop-data-grid__filter-item${option.value === selectedFilter ? ' cop-data-grid__filter-item--active' : ''}`}
                  onMouseDown={() => { onFilterChange?.(option.value); setDropdownOpen(false); }}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="cop-data-grid__toolbar-right">
        {gaShieldToggle?.enableGaShieldToggle === true && (
          <label className="cop-data-grid__ga-shield-toggle">
            <input
              type="checkbox"
              className="cop-data-grid__ga-shield-toggle-input"
              checked={gaShieldToggle.checked}
              onChange={e => gaShieldToggle.onChange(e.target.checked)}
            />
            <span className="cop-data-grid__ga-shield-toggle-track" aria-hidden="true" />
            <span className="cop-data-grid__ga-shield-toggle-label">{gaShieldToggle.label}</span>
          </label>
        )}
        {actionButton?.type === 'export' && canShowExport && (
          <div className="cop-data-grid__export" ref={exportDropdownRef}>
            <button
              type="button"
              className={`cop-data-grid__export-button${exportDropdownOpen ? ' cop-data-grid__export-button--open' : ''}`}
              onClick={() => setExportDropdownOpen(prev => !prev)} disabled={!actionButton.enabled || (gridType === 'userManagement' && hasNoRows)}
              aria-haspopup="menu" aria-expanded={exportDropdownOpen}>
              <span>{actionButton.label}</span>
              <Chevron open={exportDropdownOpen} size={14} strokeWidth={2.5} />
            </button>
            {exportDropdownOpen && (
              <ul className="cop-data-grid__export-list" role="menu">
                {actionButton.options.map(opt => (
                  <li
                    key={opt.value}
                    role="menuitem"
                    className="cop-data-grid__export-item"
                    onMouseDown={() => { opt.onClick(); setExportDropdownOpen(false); }}
                  >
                    <span className="cop-data-grid__export-item-label--desktop">{opt.label}</span>
                    <span className="cop-data-grid__export-item-label--mobile">{opt.label}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {actionButton?.type === 'action' && !isLoading && permissions.canViewCreateCase && (
          <button type="button" className="cop-data-grid__action-button"
            onClick={() => { window.location.href = actionButton.href; }} disabled={!actionButton.enabled}>
            <span className="cop-data-grid__action-label">{actionButton.label}</span>
            <span className="cop-data-grid__action-icon" aria-hidden="true" />
          </button>
        )}

        {addActionButton?.buttonType === 'add' && (
          <button type="button" className="cop-data-grid__action-button"
            onClick={() => { window.location.href = addActionButton.buttonUrl; }}>
            <span className="cop-data-grid__action-label">{addActionButton.buttonLabel}</span>
            <span className="cop-data-grid__action-icon" aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
};

export default DataGridActions;
